import { FoodsIcon, Typography } from "@foodpilot/foods";
import { Box, IconButton, Stack, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useCsrPillarDelete } from "src/api/client-api/csrStrategy/Pillars/useDeleteCsrPillar";
import { useUpsertCsrPillar } from "src/api/client-api/csrStrategy/Pillars/useUpsertCsrPillar";
import { CsrIndicator, CsrPillar } from "src/api/client-api/csrStrategy/useGetAllStrategy";
import { MODIFY, STRATEGY, useFoodpilotContext } from "src/context/FoodpilotContext";
import { useSettingsStrategy } from "src/context/Settings/CsrStrategy/useSettingsStrategy";
import { CsrPillarIcon } from "src/components/CsrPillarIcon";
import { useSnackbar } from "src/utils/useSnackbar";
import type { CsrIndicatorForm } from "../EditPillarForm/PillarForm.utils";
import { AddLeverAction } from "./AddLeverAction";
import { EmptyLevers } from "./EmptyLevers";
import { IndicatorRow } from "./IndicatorRow";
import { StrategyDeleteConfirmDialog, StrategyDeleteTarget } from "./StrategyDeleteConfirmDialog";
import { LeverDialogState, LeverUpsertDialog } from "./LeverUpsertDialog";
import { createEmptyLever, indicatorToLeverForm, leverFormToWrite } from "./UpsertDialog.utils";
import { SortableRows } from "src/components/Dragging/SortableRows/SortableRows";

type DeleteDialogState = {
    target: StrategyDeleteTarget;
    indicator?: CsrIndicator;
};

type PillarCardProps = {
    pillar: CsrPillar;
    expanded: boolean;
    onToggle: () => void;
    onEdit?: () => void;
    dragHandle?: ReactNode;
};

export const PillarCard = (props: PillarCardProps) => {
    const { pillar, expanded, onToggle, onEdit, dragHandle } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const snackbar = useSnackbar();
    const deletePillar = useCsrPillarDelete();
    const updatePillar = useUpsertCsrPillar();
    const { strategy } = useSettingsStrategy();
    const { canUser } = useFoodpilotContext();
    const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState | null>(null);
    const [leverDialog, setLeverDialog] = useState<LeverDialogState | null>(null);
    const [orderedIndicators, setOrderedIndicators] = useState(pillar.indicators);
    const canModify = canUser(MODIFY, STRATEGY);
    const leverCount = orderedIndicators.length;
    const isDeleting = deletePillar.isLoading || updatePillar.isLoading;

    useEffect(() => {
        setOrderedIndicators(pillar.indicators);
    }, [pillar.indicators]);

    const openCreateLever = () => {
        setLeverDialog({ mode: "create", indicator: createEmptyLever(orderedIndicators.length) });
    };

    const openEditLever = (indicator: CsrIndicator, rowIndex: number) => {
        setLeverDialog({ mode: "edit", indicator: indicatorToLeverForm(indicator, rowIndex) });
    };

    const saveLever = (indicator: CsrIndicatorForm) => {
        if (!strategy || !leverDialog) return;

        const savedIndicator = leverFormToWrite(indicator);
        const existingIndicators = orderedIndicators.map(({ id: _id, ...item }) => item);
        const indicators =
            leverDialog.mode === "create" ?
                [...existingIndicators, savedIndicator]
            :   existingIndicators.map((item) => (item["@id"] === savedIndicator["@id"] ? savedIndicator : item));

        updatePillar.mutate(
            {
                payload: {
                    id: pillar.id,
                    position: pillar.position,
                    name: pillar.name,
                    icon: pillar.icon,
                    strategy: strategy["@id"],
                    indicators,
                },
            },
            {
                onSuccess: () => {
                    const wasCreated = leverDialog.mode === "create";
                    setLeverDialog(null);
                    snackbar.forceClose();
                    snackbar.setSnackbarProps({
                        type: "info",
                        message:
                            wasCreated ?
                                t(
                                    "settings.csr.lever_dialog.created_success",
                                    "Your lever has been successfully added to your pillar!",
                                )
                            :   t(
                                    "settings.csr.lever_dialog.updated_success",
                                    "Your lever has been successfully updated!",
                                ),
                    });
                },
                onError: () => {
                    snackbar.forceClose();
                    snackbar.setSnackbarProps({
                        type: "error",
                        message: t("settings.error", "Error while saving"),
                    });
                },
            },
        );
    };

    const confirmDelete = () => {
        if (deleteDialog?.target === "pillar") {
            deletePillar.mutate({ pillarId: pillar.id }, { onSuccess: () => setDeleteDialog(null) });
            return;
        }

        const indicator = deleteDialog?.indicator;
        if (!indicator || !strategy) return;

        updatePillar.mutate(
            {
                payload: {
                    id: pillar.id,
                    position: pillar.position,
                    name: pillar.name,
                    icon: pillar.icon,
                    strategy: strategy["@id"],
                    indicators: orderedIndicators
                        .filter((item) => item["@id"] !== indicator["@id"])
                        .map(({ id: _id, ...indicatorToKeep }) => indicatorToKeep),
                },
            },
            { onSuccess: () => setDeleteDialog(null) },
        );
    };

    const reorderIndicators = (indicators: CsrIndicator[]) => {
        if (!strategy || updatePillar.isLoading) return;

        const previousIndicators = orderedIndicators;
        const reorderedIndicators = indicators.map((indicator, position) => ({ ...indicator, position }));
        setOrderedIndicators(reorderedIndicators);
        updatePillar.mutate(
            {
                payload: {
                    id: pillar.id,
                    position: pillar.position,
                    name: pillar.name,
                    icon: pillar.icon,
                    strategy: strategy["@id"],
                    indicators: reorderedIndicators.map(({ id: _id, ...indicator }) => indicator),
                },
            },
            {
                onError: () => {
                    setOrderedIndicators(previousIndicators);
                    snackbar.forceClose();
                    snackbar.setSnackbarProps({ type: "error", message: t("settings.error", "Error while saving") });
                },
            },
        );
    };

    return (
        <Box
            sx={{
                border: `1px solid ${theme.custom.grey[500]}`,
                borderRadius: 2,
                backgroundColor: theme.palette.common.white,
                overflow: "hidden",
            }}
        >
            <Stack direction="row" alignItems="center" gap={1.5} sx={{ px: 3, py: 2.5 }}>
                {dragHandle ?? <FoodsIcon icon="drag" size={2} />}
                <Box
                    sx={{
                        width: theme.spacing(6.5),
                        height: theme.spacing(6.5),
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                    }}
                >
                    <CsrPillarIcon icon={pillar.icon} backgroundShape="rect" height={52} />
                </Box>
                <Stack gap={0.25} sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="big-bold" component="div" sx={{ overflowWrap: "anywhere" }}>
                        {pillar.name}
                    </Typography>
                    <Typography variant="small" component="div" color={theme.custom.grey[1400]}>
                        {t("settings.csr.overview.lever_count", { defaultValue: "{{count}} lever", count: leverCount })}
                    </Typography>
                </Stack>
                {canModify && (
                    <Stack direction="row" alignItems="center" gap={0.5}>
                        <IconButton aria-label={t("settings.csr.pillars.modifypillar")} onClick={onEdit}>
                            <FoodsIcon icon="edit" size={2} />
                        </IconButton>
                        <IconButton
                            aria-label={t("settings.csr.pillars.deletepillar")}
                            onClick={() => setDeleteDialog({ target: "pillar" })}
                        >
                            <FoodsIcon icon="delete" size={2} />
                        </IconButton>
                    </Stack>
                )}
                <Box sx={{ height: theme.spacing(5), borderLeft: `1px solid ${theme.custom.grey[500]}` }} />
                <IconButton
                    aria-label={t("settings.csr.overview.toggle_pillar", "Toggle pillar")}
                    aria-expanded={expanded}
                    onClick={onToggle}
                >
                    <FoodsIcon icon={expanded ? "arrowUpShort" : "arrowDownShort"} size={2} />
                </IconButton>
            </Stack>
            {expanded && (
                <Box sx={{ borderTop: `1px solid ${theme.custom.grey[500]}`, px: { xs: 2, md: 7.5 }, py: 2.5 }}>
                    {leverCount > 0 ?
                        <Stack>
                            <SortableRows
                                items={orderedIndicators.map((indicator) => ({
                                    ...indicator,
                                    id: indicator["@id"],
                                }))}
                                disabled={!canModify || updatePillar.isLoading}
                                getHandleLabel={(indicator) =>
                                    t("settings.csr.overview.reorder_lever", {
                                        defaultValue: "Reorder {{name}}",
                                        name: indicator.leverShortName || indicator.name,
                                    })
                                }
                                onDragEnd={reorderIndicators}
                                renderItem={(indicator, indicatorDragHandle, index) => (
                                    <IndicatorRow
                                        indicator={indicator}
                                        dragHandle={indicatorDragHandle}
                                        showDivider={index < leverCount - 1}
                                        onEdit={canModify ? () => openEditLever(indicator, index) : undefined}
                                        onDelete={
                                            canModify ?
                                                () => setDeleteDialog({ target: "lever", indicator })
                                            :   undefined
                                        }
                                    />
                                )}
                            />
                            {canModify && <AddLeverAction onClick={openCreateLever} />}
                        </Stack>
                    :   <EmptyLevers onAdd={canModify ? openCreateLever : undefined} />}
                </Box>
            )}
            <StrategyDeleteConfirmDialog
                open={deleteDialog !== null}
                target={deleteDialog?.target ?? "pillar"}
                name={deleteDialog?.indicator?.leverShortName || deleteDialog?.indicator?.name || pillar.name}
                isDeleting={isDeleting}
                onConfirm={confirmDelete}
                onClose={() => setDeleteDialog(null)}
            />
            {leverDialog && (
                <LeverUpsertDialog
                    open
                    state={leverDialog}
                    isSaving={updatePillar.isLoading}
                    onClose={() => setLeverDialog(null)}
                    onSave={saveLever}
                />
            )}
        </Box>
    );
};
